import { LOGIN_CODE_LENGTH } from "../login-code";
import { codeField } from "./code-field";
import { setLoading, untilLeaving } from "./loading";
import {
  cancelled,
  offerPasskeyAutofill,
  PasskeysUnavailable,
  signInWithPasskey,
} from "./passkeys";
import { $, ApiError, api, busy, continueTo, fail, leavingLabel, say } from "./shared";

const emailForm = $<HTMLFormElement>("email-form");
const codeForm = $<HTMLFormElement>("code-form");
const emailInput = $<HTMLInputElement>("email");
const codeInput = $<HTMLInputElement>("code");
let email = "";
// The code step is the same URL, so its title is the script's to give.
const emailStepTitle = document.title;
const codeStepTitle = emailStepTitle.replace(/^[^·]+/, "Enter your code ");

const submitButton = (form: HTMLFormElement) =>
  form.querySelector<HTMLButtonElement>('button[type="submit"]');

// The last digit submits the code; the button stays for anyone who looks for it.
const field = codeInput && codeForm ? codeField(codeInput, () => codeForm.requestSubmit()) : null;

function showCodeStep() {
  if (!emailForm || !codeForm || !codeInput) return;
  emailForm.hidden = true;
  codeForm.hidden = false;
  const shown = $("code-email");
  if (shown) shown.textContent = email;
  codeInput.value = "";
  codeInput.removeAttribute("aria-invalid");
  field?.render();
  document.title = codeStepTitle;
  codeInput.focus();
}

function showEmailStep() {
  if (!emailForm || !codeForm || !emailInput) return;
  codeForm.hidden = true;
  emailForm.hidden = false;
  document.title = emailStepTitle;
  emailInput.focus();
}

emailForm?.addEventListener("submit", async (event) => {
  event.preventDefault();
  email = emailInput?.value.trim() ?? "";
  if (!email || !emailInput?.checkValidity()) {
    fail("Enter your email address, like name@example.com.");
    emailInput?.focus();
    return;
  }
  await busy(submitButton(emailForm), async () => {
    say("Sending your code…");
    try {
      await api("/email-otp/send-verification-otp", { email, type: "sign-in" });
      showCodeStep();
      say(`Code sent to ${email}.`);
    } catch (error) {
      fail(
        error instanceof ApiError && error.status === 429
          ? "Too many tries. Wait a minute."
          : "We couldn't send a code. Try again.",
      );
    }
  });
});

/** The request is done and the browser is leaving: say where to, on the button and aloud. */
function leave(button: HTMLButtonElement | null | undefined, result: { url?: string } | null) {
  const label = leavingLabel(result);
  setLoading(button, true, label);
  say(label);
  continueTo(result);
}

codeForm?.addEventListener("submit", async (event) => {
  event.preventDefault();
  const button = submitButton(codeForm);
  // Already on its way: the last digit and a press of the button send it once.
  if (button?.getAttribute("aria-busy") === "true") return;
  const otp = codeInput?.value ?? "";
  if (otp.length !== LOGIN_CODE_LENGTH) {
    fail("Enter the six-digit code from the email.");
    codeInput?.focus();
    return;
  }
  say("Signing you in…");
  try {
    // Busy from here until the page leaves; only a refusal brings the button back (#32).
    await untilLeaving(button, "Signing you in…", async () =>
      leave(button, await api<{ url?: string }>("/sign-in/email-otp", { email, otp })),
    );
  } catch (error) {
    fail(
      error instanceof ApiError && error.status === 429
        ? "Too many tries. Wait a minute."
        : "That code didn't work. Check it, or ask for a new one.",
    );
    codeInput?.setAttribute("aria-invalid", "true");
    codeInput?.select();
  }
});

// Sign-out lands here with #signed-out: say so once, and drop the fragment so a reload doesn't.
// The banner goes into the empty live region a moment after load, once a screen reader is
// watching it, so it is announced rather than read as part of the page.
const signedOut = $<HTMLTemplateElement>("signed-out");
const notice = $("notice");
if (signedOut && notice && location.hash === "#signed-out") {
  history.replaceState(null, "", location.pathname + location.search);
  setTimeout(() => notice.replaceChildren(signedOut.content.cloneNode(true)), 250);
}

$("change-email")?.addEventListener("click", () => {
  say("");
  showEmailStep();
});

// On load, where the browser supports it, the email box also offers the device's passkeys; the
// button below stays for browsers that don't and for people who look for it.
if (emailInput) {
  offerPasskeyAutofill().then(
    (result) => {
      if (result) leave($<HTMLButtonElement>("passkey-sign-in"), result);
    },
    (error) =>
      fail(
        error instanceof PasskeysUnavailable
          ? "Passkeys aren't available right now. Use an email code instead."
          : "That passkey didn't work here. Use an email code instead.",
      ),
  );
}

$<HTMLButtonElement>("passkey-sign-in")?.addEventListener("click", async (event) => {
  const button = event.currentTarget as HTMLButtonElement;
  say("Waiting for your passkey…");
  try {
    await untilLeaving(button, "Waiting for your passkey…", async () =>
      leave(button, await signInWithPasskey()),
    );
  } catch (error) {
    if (cancelled(error)) say("");
    else fail("That passkey didn't work here. Use an email code instead.");
  }
});

for (const [id, accept] of [
  ["consent-allow", true],
  ["consent-deny", false],
] as const) {
  $<HTMLButtonElement>(id)?.addEventListener("click", async (event) => {
    const button = event.currentTarget as HTMLButtonElement;
    try {
      await untilLeaving(button, accept ? "Allowing…" : "Declining…", async () =>
        leave(button, await api<{ url?: string }>("/oauth2/consent", { accept })),
      );
    } catch {
      fail("This request has expired. Go back to the app and try again.");
    }
  });
}
